import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ChevronDown, Shield, TrendingUp, PieChart, Lock } from 'lucide-react';

const StoryHero = () => {
  const scrollToStory = () => {
    const element = document.getElementById('journey-section');
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen bg-deep-space overflow-hidden flex items-center" data-testid="story-hero">
      {/* Grid Background */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" />
      
      {/* Gradient overlays */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-tech-blue/10 rounded-full blur-[150px]" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-missed-gold/5 rounded-full blur-[120px]" />
      
      <div className="relative w-full max-w-7xl mx-auto px-6 lg:px-8 py-24">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Text Content */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            {/* Beta Badge */}
            <div className="inline-flex items-center gap-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full px-4 py-2">
              <span className="w-2 h-2 rounded-full bg-wealth-green animate-pulse" />
              <span className="text-sm text-slate-300 font-ibm-plex">BETA: 1 Month Free License</span>
            </div>
            
            {/* Headline - Optimized for USP + Multi-Asset */}
            <h1 className="font-outfit tracking-tighter">
              <span className="block text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-light text-white/90">
                Your Complete Wealth
              </span>
              <span className="block text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent mt-2">
                On Your PC.
              </span>
              <span className="block text-2xl sm:text-3xl lg:text-4xl font-semibold text-tech-blue mt-4">
                Real Insights. Not Assumptions.
              </span>
            </h1>
            
            {/* Subheadline - Value Proposition */}
            <p className="text-lg lg:text-xl text-slate-400 font-ibm-plex leading-relaxed max-w-xl">
              Track <span className="text-white font-medium">Mutual Funds, Stocks, Gold, FD, EPF, NPS</span> and more — all in one private dashboard. 
              Compare your portfolio against benchmarks using your <span className="text-missed-gold font-medium">exact investment dates</span>, 
              not generic returns. Know the <span className="text-white font-medium">real picture</span>.
            </p>
            
            {/* Key Value Props */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-tech-blue/20 flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-5 h-5 text-tech-blue" />
                </div>
                <div>
                  <div className="text-white font-medium text-sm">Portfolio vs Benchmark</div>
                  <div className="text-slate-400 text-xs">Your dates, your reality</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-wealth-green/20 flex items-center justify-center flex-shrink-0">
                  <PieChart className="w-5 h-5 text-wealth-green" />
                </div>
                <div>
                  <div className="text-white font-medium text-sm">10+ Asset Types</div>
                  <div className="text-slate-400 text-xs">One unified view</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-missed-gold/20 flex items-center justify-center flex-shrink-0">
                  <Lock className="w-5 h-5 text-missed-gold" />
                </div>
                <div>
                  <div className="text-white font-medium text-sm">100% Private</div>
                  <div className="text-slate-400 text-xs">Data stays on your PC</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-alert-red/20 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-alert-red" />
                </div>
                <div>
                  <div className="text-white font-medium text-sm">Actionable Insights</div>
                  <div className="text-slate-400 text-xs">Hold, exit, or increase</div>
                </div>
              </div>
            </div>
            
            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Link 
                to="/login" 
                className="group inline-flex items-center justify-center gap-2 bg-gradient-to-r from-tech-blue to-blue-600 hover:from-blue-600 hover:to-tech-blue text-white px-8 py-4 rounded-xl font-semibold text-lg shadow-lg shadow-tech-blue/25 hover:shadow-xl hover:shadow-tech-blue/30 transition-all duration-300"
                data-testid="hero-cta-primary"
              >
                Get Free License
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </motion.div>
          
          {/* Right: Visual Element - The Discovery Preview */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="hidden lg:block relative"
          >
            <div className="relative">
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-tech-blue/20 to-missed-gold/20 rounded-2xl blur-3xl" />
              
              {/* Card - The Insight Preview */}
              <div className="relative bg-white/5 backdrop-blur-2xl border border-white/10 rounded-2xl p-8 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
                <div className="space-y-6">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-alert-red" />
                    <div className="w-3 h-3 rounded-full bg-missed-gold" />
                    <div className="w-3 h-3 rounded-full bg-wealth-green" />
                    <span className="text-sm text-slate-400 font-ibm-plex ml-2">Portfolio Auditor</span>
                  </div>
                  
                  <div className="text-sm text-slate-400 font-ibm-plex uppercase tracking-wider">The Discovery That Changed Everything</div>
                  
                  <div className="space-y-4">
                    <div className="bg-deep-space-surface/50 rounded-xl p-4 border border-white/5">
                      <p className="text-slate-300 font-ibm-plex text-base">
                        "I thought my ₹63L portfolio was doing great..."
                      </p>
                      <p className="text-missed-gold font-ibm-plex text-base mt-2 font-medium">
                        "...until I discovered Gold would have given me ₹98L"
                      </p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-wealth-green/10 border border-wealth-green/20 rounded-xl p-4 text-center">
                        <div className="font-jetbrains text-sm font-bold text-wealth-green">My Portfolio</div>
                        <div className="font-jetbrains text-2xl font-bold text-white mt-1">₹63L</div>
                      </div>
                      <div className="bg-missed-gold/10 border border-missed-gold/20 rounded-xl p-4 text-center">
                        <div className="font-jetbrains text-sm font-bold text-missed-gold">If Gold</div>
                        <div className="font-jetbrains text-2xl font-bold text-white mt-1">₹98L</div>
                      </div>
                    </div>
                    
                    <div className="text-center py-2">
                      <span className="font-jetbrains text-3xl font-bold text-alert-red">-₹35L</span>
                      <p className="text-slate-400 text-sm mt-1 font-ibm-plex">The gap I never knew existed</p>
                    </div>
                    
                    <div className="bg-tech-blue/10 border border-tech-blue/20 rounded-xl p-3 text-center">
                      <p className="text-sm text-slate-300 font-ibm-plex">
                        <span className="text-tech-blue font-medium">This is real.</span> Same amounts. Same dates. Different outcome.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
        
        {/* Scroll Indicator */}
        <motion.button
          onClick={scrollToStory}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-slate-400 hover:text-white transition-colors cursor-pointer"
          data-testid="scroll-indicator"
        >
          <span className="text-sm font-ibm-plex">See how I built this tool</span>
          <ChevronDown className="w-6 h-6 animate-bounce" />
        </motion.button>
      </div>
    </section>
  );
};

export default StoryHero;
